import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Card from 'components/card';
import { CONFIG } from 'variables/config';
import { MdBlock, MdDelete, MdAdd } from 'react-icons/md';

type BlacklistType = 'phone' | 'email' | 'firstName' | 'lastName';

interface BlacklistEntry {
    _id: string;
    type: BlacklistType;
    value: string;
    reason: string;
    blockedBy: string;
    createdAt: string;
}

const TYPE_LABELS: Record<BlacklistType, string> = {
    phone: 'Phone',
    email: 'Email',
    firstName: 'First name',
    lastName: 'Last name',
};

export default function BackOfficeLeadFieldBlacklist() {
    const [entries, setEntries] = useState<BlacklistEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [newType, setNewType] = useState<BlacklistType>('phone');
    const [newValue, setNewValue] = useState('');
    const [newReason, setNewReason] = useState('');
    const [adding, setAdding] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [filter, setFilter] = useState<'all' | BlacklistType>('all');

    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

    const fetchEntries = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${CONFIG.apiUrl}/admin/lead-field-blacklist`, { headers });
            setEntries(res.data);
        } catch (e) {
            setError('Failed to load blacklist.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEntries();
    }, []);

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        if (!newValue.trim()) return;
        try {
            setAdding(true);
            await axios.post(
                `${CONFIG.apiUrl}/admin/lead-field-blacklist`,
                { type: newType, value: newValue.trim(), reason: newReason.trim() },
                { headers }
            );
            setSuccess(`${TYPE_LABELS[newType]} "${newValue.trim()}" blacklisted.`);
            setNewValue('');
            setNewReason('');
            fetchEntries();
        } catch (e: any) {
            setError(e.response?.data?.message || 'Error adding entry.');
        } finally {
            setAdding(false);
        }
    };

    const handleDelete = async (entry: BlacklistEntry) => {
        setError('');
        setSuccess('');
        try {
            await axios.delete(`${CONFIG.apiUrl}/admin/lead-field-blacklist/${entry._id}`, { headers });
            setSuccess(`${TYPE_LABELS[entry.type]} "${entry.value}" removed.`);
            fetchEntries();
        } catch (e: any) {
            setError(e.response?.data?.message || 'Error removing entry.');
        }
    };

    const filteredEntries = useMemo(
        () => (filter === 'all' ? entries : entries.filter((entry) => entry.type === filter)),
        [entries, filter]
    );

    return (
        <div className="pt-[50px] flex flex-col gap-6">
            <Card extra="p-5">
                <div className="mb-6 flex items-center gap-3">
                    <MdBlock className="text-2xl text-red-500" />
                    <h2 className="text-2xl font-bold text-navy-700 dark:text-white">Lead Field Blacklist</h2>
                    <span className="ml-2 rounded-full bg-red-100 px-3 py-0.5 text-sm font-semibold text-red-700">
                        {entries.length} blocked
                    </span>
                </div>

                <form onSubmit={handleAdd} className="mb-6 flex flex-wrap items-end gap-3 rounded-xl bg-gray-50 p-4 dark:bg-navy-800">
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Type *</label>
                        <select
                            value={newType}
                            onChange={(e) => setNewType(e.target.value as BlacklistType)}
                            className="w-40 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-red-400 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                        >
                            {(Object.keys(TYPE_LABELS) as BlacklistType[]).map((type) => (
                                <option key={type} value={type}>{TYPE_LABELS[type]}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Value *</label>
                        <input
                            type="text"
                            value={newValue}
                            onChange={(e) => setNewValue(e.target.value)}
                            placeholder="e.g. test@test.com"
                            className="w-56 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-red-400 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                            required
                        />
                    </div>
                    <div className="flex-1 min-w-[180px]">
                        <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Reason</label>
                        <input
                            type="text"
                            value={newReason}
                            onChange={(e) => setNewReason(e.target.value)}
                            placeholder="e.g. Repeat spam submissions"
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-red-400 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={adding}
                        className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                    >
                        <MdAdd className="text-lg" />
                        {adding ? 'Blocking...' : 'Block'}
                    </button>
                </form>

                {error && (
                    <div className="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">{error}</div>
                )}
                {success && (
                    <div className="mb-4 rounded-lg bg-green-100 p-3 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">{success}</div>
                )}

                <div className="mb-4 flex gap-2">
                    {(['all', ...Object.keys(TYPE_LABELS)] as ('all' | BlacklistType)[]).map((type) => (
                        <button
                            key={type}
                            onClick={() => setFilter(type)}
                            className={`rounded-full px-3 py-1 text-xs font-semibold ${
                                filter === type
                                    ? 'bg-red-600 text-white'
                                    : 'bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-gray-300'
                            }`}
                        >
                            {type === 'all' ? 'All' : TYPE_LABELS[type]}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="py-10 text-center text-gray-400">Loading...</div>
                ) : filteredEntries.length === 0 ? (
                    <div className="py-10 text-center text-gray-400">No entries blacklisted.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-navy-600">
                                    <th className="pb-3 pr-6 text-xs font-bold uppercase text-gray-500">Type</th>
                                    <th className="pb-3 pr-6 text-xs font-bold uppercase text-gray-500">Value</th>
                                    <th className="pb-3 pr-6 text-xs font-bold uppercase text-gray-500">Reason</th>
                                    <th className="pb-3 pr-6 text-xs font-bold uppercase text-gray-500">Blocked by</th>
                                    <th className="pb-3 pr-6 text-xs font-bold uppercase text-gray-500">Date</th>
                                    <th className="pb-3 text-xs font-bold uppercase text-gray-500">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEntries.map((entry) => (
                                    <tr key={entry._id} className="border-b border-gray-100 dark:border-navy-700">
                                        <td className="py-3 pr-6">
                                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600 dark:bg-navy-700 dark:text-gray-300">
                                                {TYPE_LABELS[entry.type]}
                                            </span>
                                        </td>
                                        <td className="py-3 pr-6 font-mono font-semibold text-red-600">{entry.value}</td>
                                        <td className="py-3 pr-6 text-gray-600 dark:text-gray-400">{entry.reason || '—'}</td>
                                        <td className="py-3 pr-6 text-gray-500">{entry.blockedBy || '—'}</td>
                                        <td className="py-3 pr-6 text-gray-500">
                                            {new Date(entry.createdAt).toLocaleString('fr-FR')}
                                        </td>
                                        <td className="py-3">
                                            <button
                                                onClick={() => handleDelete(entry)}
                                                className="flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-50 dark:bg-navy-700 dark:hover:bg-red-900/30"
                                            >
                                                <MdDelete />
                                                Remove
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
}
